import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InfoIcon from "@mui/icons-material/Info";

const TaxRegimeSelector = ({
  selectedRegime,
  onRegimeChange,
  onConfirmRegimeChange,
  isUpdatingProfile,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingRegimeChange, setPendingRegimeChange] = useState(null);
  // Função para iniciar o processo de troca de regime
  const handleRegimeChangeRequest = (regimeId) => {
    // Se já for o regime selecionado, não faz nada
    if (regimeId === selectedRegime) return;

    // Se não houver regime selecionado anteriormente (primeira seleção), seleciona diretamente
    if (!selectedRegime) {
      onRegimeChange(regimeId);
      return;
    }

    // Se já existir um regime selecionado, mostra o diálogo de confirmação
    setPendingRegimeChange(regimeId);
    setDialogOpen(true);
  };

  // Função para confirmar a troca de regime
  const handleConfirmRegimeChange = () => {
    // Efetua a troca de regime localmente
    onRegimeChange(pendingRegimeChange);

    // Chama a função para atualizar o tax_profile no backend
    if (onConfirmRegimeChange) {
      onConfirmRegimeChange(pendingRegimeChange);
    }

    // Fecha o diálogo
    setDialogOpen(false);
    setPendingRegimeChange(null);
  };

  // Função para cancelar a troca de regime
  const handleCancelRegimeChange = () => {
    setDialogOpen(false);
    setPendingRegimeChange(null);
  };

  const regimes = [
    {
      id: "LUCRO_REAL",
      title: "Lucro Real",
      description: "Tributação sobre o lucro efetivo da empresa",
      icon: <AccountBalanceIcon sx={{ fontSize: 36 }} />,
      color: "#1976d2",
      faixaFaturamento: "Acima de R$ 78 milhões (obrigatório)",
      features: [
        "Dedução completa de despesas",
        "Compensação de prejuízos",
        "Regime obrigatório para grandes empresas",
      ],
      idealPara: [
        "Grandes empresas",
        "Empresas com muitas despesas dedutíveis",
        "Empresas com prejuízos a compensar",
      ],
    },
    {
      id: "SIMPLES_NACIONAL",
      title: "Simples Nacional",
      description: "Regime simplificado para micro e pequenas empresas",
      icon: <BusinessIcon sx={{ fontSize: 36 }} />,
      color: "#2e7d32",
      faixaFaturamento: "Até R$ 4,8 milhões anuais",
      features: [
        "Alíquotas reduzidas",
        "Cálculo simplificado",
        "Unificação de tributos",
      ],
      idealPara: [
        "Microempresas (ME)",
        "Empresas de Pequeno Porte (EPP)",
        "Startups em crescimento",
      ],
    },
    {
      id: "LUCRO_PRESUMIDO",
      title: "Lucro Presumido",
      description: "Tributação sobre lucro presumido por atividade",
      icon: <TrendingUpIcon sx={{ fontSize: 36 }} />,
      color: "#ed6c02",
      faixaFaturamento: "Até R$ 78 milhões anuais",
      features: [
        "Percentual fixo de presunção",
        "Menor complexidade",
        "Ideal para margens altas",
      ],
      idealPara: [
        "Empresas com alta margem de lucro",
        "Prestadoras de serviços",
        "Empresas de médio porte",
      ],
    },
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          mb: 3,
          color: "#2c3e50",
        }}
      >
        Regime Tributário
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        {regimes.map((regime) => {
          const isSelected = selectedRegime === regime.id;

          return (
            <Box
              key={regime.id}
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 calc(33.333% - 16px)" },
                minWidth: { xs: "100%", sm: "280px" },
              }}
            >
              <Card
                onClick={() => handleRegimeChangeRequest(regime.id)}
                sx={{
                  height: "100%",
                  position: "relative",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: isSelected
                    ? `0 6px 20px ${regime.color}40`
                    : "0 2px 12px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                  border: isSelected
                    ? `2px solid ${regime.color}`
                    : "1px solid #e0e0e0",
                  "&:hover": {
                    boxShadow: `0 8px 25px ${regime.color}25`,
                    cursor: "pointer",
                  },
                }}
              >
                {/* Cabeçalho do Card */}
                <Box
                  sx={{
                    bgcolor: isSelected ? `${regime.color}15` : "#f8f9fa",
                    borderBottom: `1px solid ${
                      isSelected ? `${regime.color}30` : "#eaeaea"
                    }`,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        bgcolor: `${regime.color}20`,
                        color: regime.color,
                        p: 1,
                        borderRadius: "10px",
                        display: "flex",
                        mr: 1.5,
                      }}
                    >
                      {regime.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: isSelected ? regime.color : "text.primary",
                      }}
                    >
                      {regime.title}
                    </Typography>
                  </Box>

                  {isSelected && (
                    <Chip
                      size="small"
                      icon={
                        <CheckCircleRoundedIcon
                          sx={{ fontSize: "0.9rem !important" }}
                        />
                      }
                      label="Ativo"
                      sx={{
                        bgcolor: `${regime.color}15`,
                        color: regime.color,
                        fontWeight: 600,
                        border: `1px solid ${regime.color}40`,
                        height: "24px",
                      }}
                    />
                  )}
                </Box>
                {/* Corpo do Card */}
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      color: "text.secondary",
                      fontSize: "0.85rem",
                    }}
                  >
                    {regime.description}
                  </Typography>

                  {/* Faixa de Faturamento */}
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      bgcolor: isSelected ? `${regime.color}08` : "#f2f4f7",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: isSelected ? regime.color : "#555",
                      }}
                    >
                      Faturamento:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontSize: "0.75rem" }}
                    >
                      {regime.faixaFaturamento}
                    </Typography>
                  </Box>

                  {/* Sempre mostrar as características sem Collapse */}
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: "0.8rem",
                        color: isSelected ? regime.color : "#555",
                      }}
                    >
                      Características
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      {regime.features.map((feature, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            color: "text.secondary",
                            mb: 0.5,
                            fontSize: "0.75rem",
                            "&:before": {
                              content: '""',
                              display: "block",
                              width: "5px",
                              height: "5px",
                              mt: "6px",
                              mr: 1,
                              bgcolor: regime.color,
                              borderRadius: "50%",
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: "0.8rem",
                        color: isSelected ? regime.color : "#555",
                      }}
                    >
                      Ideal para
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 2,
                      }}
                    >
                      {regime.idealPara.map((item, index) => (
                        <Chip
                          key={index}
                          label={item}
                          size="small"
                          sx={{
                            height: "20px",
                            fontSize: "0.65rem",
                            bgcolor: isSelected
                              ? `${regime.color}15`
                              : "#f0f0f0",
                          }}
                        />
                      ))}
                    </Box>
                  </>

                  {/* Status de seleção */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      mt: 2,
                    }}
                  >
                    {isSelected && (
                      <IconButton
                        size="small"
                        disabled
                        sx={{
                          color: regime.color,
                          bgcolor: `${regime.color}15`,
                        }}
                      >
                        <CheckCircleRoundedIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Modal de confirmação redesenhado */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelRegimeChange}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 4,
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            boxShadow:
              "0 20px 40px rgba(0,0,0,0.1), 0 4px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header com ícone de aviso */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 4,
            pb: 2,
            background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
            color: "white",
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
              backdropFilter: "blur(10px)",
            }}
          >
            <SwapHorizIcon sx={{ fontSize: 40, color: "white" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textAlign: "center",
              mb: 1,
            }}
          >
            Alterar Regime Tributário
          </Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              textAlign: "center",
              maxWidth: 400,
            }}
          >
            Esta ação impactará diretamente os cálculos fiscais da sua empresa
          </Typography>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Seção de comparação visual */}
          <Box sx={{ p: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "#fff",
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                {/* Regime atual */}
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Regime Atual
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#334155",
                      mt: 1,
                    }}
                  >
                    {regimes.find((r) => r.id === selectedRegime)?.title ||
                      "Nenhum"}
                  </Typography>
                </Box>

                {/* Seta de mudança */}
                <Box sx={{ mx: 3 }}>
                  <SwapHorizIcon
                    sx={{
                      color: "#f97316",
                      fontSize: 32,
                      "@keyframes pulse": {
                        "0%, 100%": {
                          transform: "scale(1)",
                          opacity: 1,
                        },
                        "50%": {
                          transform: "scale(1.1)",
                          opacity: 0.8,
                        },
                      },
                      animation: "pulse 2s infinite",
                    }}
                  />
                </Box>

                {/* Novo regime */}
                <Box
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    p: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                    color: "white",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Novo Regime
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mt: 1,
                    }}
                  >
                    {regimes.find((r) => r.id === pendingRegimeChange)?.title}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Alert de impacto */}
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid #fed7aa",
                backgroundColor: "#fef3c7",
                "& .MuiAlert-message": {
                  color: "#92400e",
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Importante: Esta alteração afetará:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <li>Cálculos de margem de todos os produtos</li>
                <li>Impostos aplicados nas vendas futuras</li>
                <li>Relatórios fiscais e financeiros</li>
              </Box>
            </Alert>

            {/* Informações adicionais */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: "#f0f9ff",
                borderRadius: 3,
                border: "1px solid #bae6fd",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <InfoIcon sx={{ color: "#0284c7", mt: 0.5 }} />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#0c4a6e",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    💡 Dica Profissional
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#0c4a6e", lineHeight: 1.6 }}
                  >
                    Recomendamos consultar um contador antes de alterar o regime
                    tributário. Você pode reverter esta alteração a qualquer
                    momento, mas é importante entender as implicações fiscais
                    para sua empresa.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        {/* Footer com botões melhorados */}
        <DialogActions
          sx={{
            p: 4,
            pt: 0,
            gap: 2,
            backgroundColor: "#f8fafc",
          }}
        >
          <Button
            onClick={handleCancelRegimeChange}
            disabled={isUpdatingProfile}
            variant="outlined"
            size="large"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": {
                borderColor: "#cbd5e1",
                backgroundColor: "#f8fafc",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmRegimeChange}
            disabled={isUpdatingProfile}
            variant="contained"
            size="large"
            startIcon={
              isUpdatingProfile ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
              boxShadow: "0 4px 12px rgba(249, 115, 22, 0.25)",
              "&:hover": {
                background: "linear-gradient(135deg, #ea580c 0%, #dc2626 100%)",
                boxShadow: "0 6px 16px rgba(249, 115, 22, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#cbd5e1",
                color: "#94a3b8",
              },
            }}
          >
            {isUpdatingProfile ? "Processando..." : "Confirmar Alteração"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxRegimeSelector;
